import React from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Trash2, RotateCcw } from 'lucide-react';

/**
 * One editable proposal line, for every line type the app creates:
 * theatre_ads, newspaper_eenadu, messaging_campaign, custom and the
 * product-catalog lines. Amounts are always recomputed by the server on
 * save; this component only edits the inputs.
 *
 * Props:
 *   line, index           — the line and its position
 *   onChange(idx, patch)  — merge `patch` into the line
 *   onRemove(idx)
 *   durationOptions       — theatre spot lengths in seconds
 *   dirty                 — true when lines changed since the last save
 */

const fmt = (n) => (n == null || Number.isNaN(Number(n))
  ? '—'
  : `₹${Number(n).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`);

// Keep '' while the user is typing; store numbers otherwise.
const numOrBlank = (v) => (v === '' ? '' : Number(v));

function Field({ label, children, className = '' }) {
  return (
    <label className={`flex flex-col gap-0.5 text-[10px] font-medium uppercase tracking-wide text-ink-2 ${className}`}>
      {label}
      {children}
    </label>
  );
}

function NumInput({ value, onChange, testId, min = 0, step = 'any', className = 'w-24' }) {
  return (
    <Input type="number" min={min} step={step} className={`h-8 text-xs ${className}`}
           value={value ?? ''} onChange={(e) => onChange(numOrBlank(e.target.value))}
           data-testid={testId} />
  );
}

function TextInput({ value, onChange, testId, placeholder, className = 'w-full' }) {
  return (
    <Input className={`h-8 text-xs ${className}`} value={value ?? ''} placeholder={placeholder}
           onChange={(e) => onChange(e.target.value)} data-testid={testId} />
  );
}

function TextArea({ value, onChange, testId, placeholder }) {
  return (
    <textarea className="w-full min-h-[52px] rounded-md border border-ink/15 bg-white p-2 text-xs normal-case tracking-normal font-normal text-ink"
              value={value ?? ''} placeholder={placeholder}
              onChange={(e) => onChange(e.target.value)} data-testid={testId} />
  );
}

export default function ProposalLineEditor({ line, index, onChange, onRemove, durationOptions = [10, 20, 30, 40, 50, 60], dirty }) {
  const set = (patch) => onChange(index, patch);
  const svc = line.service_key || 'custom';
  const isCatalog = svc.startsWith('product_catalog');
  const amount = line.line_total ?? line.amount_after_slab ?? line.amount;

  let title = null;
  let fields = null;

  if (svc === 'theatre_ads') {
    const hasOverride = line.rate_override !== undefined && line.rate_override !== null && line.rate_override !== '';
    title = (
      <div className="text-xs text-ink">
        {(line.attributes?.theatre || line.attributes?.theatre_code || 'Theatre')} · {line.network} · {(line.rate_type || '').toUpperCase()}
      </div>
    );
    fields = (
      <>
        <Field label="Screens"><NumInput min={1} step={1} value={line.screens} onChange={(v) => set({ screens: v })} testId={`line-screens-${index}`} className="w-20" /></Field>
        {/* Changing weeks drops the end date so the week count wins. */}
        <Field label="Weeks"><NumInput min={1} step={1} value={line.weeks} onChange={(v) => set({ weeks: v, date_to: null })} testId={`line-weeks-${index}`} className="w-20" /></Field>
        <Field label="Spot length">
          <Select value={String(line.duration_seconds || durationOptions[0] || 10)} onValueChange={(v) => set({ duration_seconds: +v })}>
            <SelectTrigger className="h-8 w-[90px] text-xs" data-testid={`line-duration-${index}`}><SelectValue /></SelectTrigger>
            <SelectContent>
              {durationOptions.map((d) => <SelectItem key={d} value={String(d)}>{d} sec</SelectItem>)}
            </SelectContent>
          </Select>
        </Field>
        <Field label="Rate / screen / week (₹)">
          <div className="flex items-center gap-1">
            <NumInput value={hasOverride ? line.rate_override : line.rate}
                      onChange={(v) => set({ rate_override: v })} testId={`line-rate-${index}`} className="w-28" />
            {hasOverride && (
              <Button type="button" size="sm" variant="ghost" className="h-8 px-2 text-[11px] normal-case"
                      title="Go back to the rate card price"
                      onClick={() => set(line.rate_item_id
                        ? { rate_override: null, rate: null, base_rate: null, weekly_breakup: [] }
                        : { rate_override: null })}
                      data-testid={`line-rate-reset-${index}`}>
                <RotateCcw className="h-3 w-3 mr-1" /> Rate card
              </Button>
            )}
          </div>
        </Field>
      </>
    );
  } else if (svc === 'newspaper_eenadu') {
    title = <div className="text-xs text-ink">{line.newspaper} · {line.edition} · {line.colour}</div>;
    fields = (
      <>
        <Field label="Width (cm)"><NumInput value={line.width_cm} onChange={(v) => set({ width_cm: v })} testId={`line-width-${index}`} className="w-20" /></Field>
        <Field label="Height (cm)"><NumInput value={line.height_cm} onChange={(v) => set({ height_cm: v })} testId={`line-height-${index}`} className="w-20" /></Field>
        <Field label="Insertions"><NumInput min={1} step={1} value={line.insertions} onChange={(v) => set({ insertions: v })} testId={`line-insertions-${index}`} className="w-20" /></Field>
        <Field label="Rate / sq cm (₹)"><NumInput value={line.rate_per_sqcm} onChange={(v) => set({ rate_per_sqcm: v })} testId={`line-rate-${index}`} /></Field>
      </>
    );
  } else if (svc === 'messaging_campaign') {
    title = <div className="text-xs text-ink">{line.channel_label || line.channel_key}</div>;
    fields = (
      <>
        <Field label={`Quantity (${line.unit_label || 'units'})`}><NumInput min={0} step={1} value={line.recipients} onChange={(v) => set({ recipients: v })} testId={`line-qty-${index}`} className="w-28" /></Field>
        {line.channel_key === 'sms_promotional' && (
          <Field label="SMS parts"><NumInput min={1} step={1} value={line.sms_segments} onChange={(v) => set({ sms_segments: v })} testId={`line-segments-${index}`} className="w-20" /></Field>
        )}
        <Field label={`Rate / ${line.unit_label || 'unit'} (₹)`}><NumInput value={line.rate} onChange={(v) => set({ rate: v, rate_overridden: true })} testId={`line-rate-${index}`} /></Field>
        <Field label="Billing event" className="min-w-[160px] flex-1"><TextInput value={line.billing_event} onChange={(v) => set({ billing_event: v })} testId={`line-billing-${index}`} placeholder="per message delivered" /></Field>
        <Field label="Description" className="basis-full"><TextArea value={line.description} onChange={(v) => set({ description: v })} testId={`line-desc-${index}`} placeholder="Shown on the proposal under this line" /></Field>
      </>
    );
  } else {
    // custom, product_catalog_item, product_catalog_custom and anything else
    // priced as quantity × rate × duration.
    fields = (
      <>
        <Field label="Item" className="min-w-[180px] flex-1"><TextInput value={line.item} onChange={(v) => set({ item: v })} testId={`line-item-${index}`} /></Field>
        <Field label="Qty"><NumInput value={line.quantity} onChange={(v) => set({ quantity: v })} testId={`line-qty-${index}`} className="w-20" /></Field>
        <Field label="Rate (₹)"><NumInput value={line.rate} onChange={(v) => set({ rate: v })} testId={`line-rate-${index}`} /></Field>
        {(isCatalog || line.duration != null) && (
          <Field label={`Duration${line.unit_type ? ` (${line.unit_type})` : ''}`}><NumInput min={1} value={line.duration} onChange={(v) => set({ duration: v })} testId={`line-duration-${index}`} className="w-20" /></Field>
        )}
        {isCatalog && (
          <Field label="GST %"><NumInput value={line.gst_pct} onChange={(v) => set({ gst_pct: v })} testId={`line-gst-${index}`} className="w-16" /></Field>
        )}
        <Field label="Description" className="basis-full"><TextArea value={line.description} onChange={(v) => set({ description: v, ...(isCatalog ? { description_edited: true } : {}) })} testId={`line-desc-${index}`} placeholder="Optional, prints smaller under the item" /></Field>
      </>
    );
  }

  return (
    <div className="rounded-lg border border-ink/10 p-3 space-y-2" data-testid={`line-editor-${index}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-xs text-ink-2">{index + 1}</span>
          <Badge variant="outline">{svc.replace(/_/g, ' ')}</Badge>
          {title}
        </div>
        <div className="flex items-center gap-2">
          <div className="text-right">
            <div className="text-sm font-semibold text-ink" data-testid={`line-amount-${index}`}>{dirty ? '—' : fmt(amount)}</div>
            {dirty && <div className="text-[10px] text-ink-2">Save to recalculate</div>}
          </div>
          <Button type="button" size="sm" variant="ghost" onClick={() => onRemove(index)} data-testid={`line-remove-${index}`}>
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>
      <div className="flex flex-wrap items-end gap-3">{fields}</div>
    </div>
  );
}
